import { useEffect, useState } from 'react';
import type { SoundPrefs } from '../game/sound';
import { getPrefs, play, setMuted, setVolume, subscribe } from '../game/sound';

/**
 * Mute and volume.
 *
 * Always reachable, because sound somebody did not ask for needs an off switch
 * they do not have to hunt for. It lives in the footer, which is on every panel
 * screen, and the setting is remembered.
 *
 * Nothing here can make a noise on load: browsers refuse to start an
 * AudioContext before a user gesture, and the first cue is always a click.
 */
export function SoundControl() {
  const [prefs, setPrefs] = useState<SoundPrefs>(() => getPrefs());
  const [open, setOpen] = useState(false);

  // The preference is module state shared with every cue site, so this stays
  // correct if something else changes it.
  useEffect(() => subscribe(setPrefs), []);

  return (
    <span className="sound">
      <button
        type="button"
        className="sound__toggle"
        aria-pressed={prefs.muted}
        aria-label={prefs.muted ? 'Unmute sound' : 'Mute sound'}
        onClick={() => {
          const next = !prefs.muted;
          setMuted(next);
          // Confirm unmuting with an audible cue; muting is confirmed by the
          // silence, and playing a sound to say "sound is off" is absurd.
          if (!next) play('select');
        }}
      >
        <span aria-hidden="true">{prefs.muted ? '🔇' : '🔊'}</span>
      </button>

      <button
        type="button"
        className="sound__more"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        Sound
      </button>

      {open && (
        <span className="sound__panel">
          <label className="sound__label" htmlFor="volume">
            Volume
          </label>
          <input
            id="volume"
            className="sound__slider"
            type="range"
            min={0}
            max={100}
            value={Math.round(prefs.volume * 100)}
            onChange={(event) => setVolume(Number(event.target.value) / 100)}
            // Preview on release rather than on every step, or dragging the
            // slider fires a hundred overlapping cues.
            onPointerUp={() => play('move')}
            onKeyUp={() => play('move')}
          />
          <span className="sound__value">{Math.round(prefs.volume * 100)}%</span>
        </span>
      )}
    </span>
  );
}
